import { type ReactNode, useRef } from 'react';

import { useReducedMotion } from '@/hooks/use-reduced-motion';
import { gsap, useGSAP } from '@/lib/gsap';
import { DURATION, GSAP_EASE } from '@/lib/motion-tokens';
import { cn } from '@/lib/utils';

export interface TiltCardProps {
  children: ReactNode;
  className?: string;
  /** Maximum tilt in degrees at the edges. */
  max?: number;
  /** Perspective distance (px). Lower = stronger 3D. */
  perspective?: number;
  /** Show a subtle gold glare that follows the pointer. */
  glare?: boolean;
}

/**
 * Cursor-driven 3D tilt for a card or media tile. The element leans toward the
 * pointer (rotateX/rotateY via quickTo for 60fps) and settles flat on leave,
 * with an optional gold glare. Disabled on touch `(hover: none)` and reduced
 * motion, where it renders as a plain static card.
 */
export function TiltCard({
  children,
  className,
  max = 8,
  perspective = 900,
  glare = true,
}: TiltCardProps) {
  const ref = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const glareRef = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion();

  useGSAP(
    () => {
      const el = ref.current;
      const inner = innerRef.current;
      if (!el || !inner || reduced) return;
      if (window.matchMedia('(hover: none)').matches) return;

      const rotX = gsap.quickTo(inner, 'rotationX', {
        duration: DURATION.fast,
        ease: GSAP_EASE.out,
      });
      const rotY = gsap.quickTo(inner, 'rotationY', {
        duration: DURATION.fast,
        ease: GSAP_EASE.out,
      });
      const glareX = glareRef.current
        ? gsap.quickTo(glareRef.current, 'xPercent', {
            duration: DURATION.fast,
            ease: GSAP_EASE.out,
          })
        : null;
      const glareY = glareRef.current
        ? gsap.quickTo(glareRef.current, 'yPercent', {
            duration: DURATION.fast,
            ease: GSAP_EASE.out,
          })
        : null;

      const onMove = (event: PointerEvent) => {
        const rect = el.getBoundingClientRect();
        const px = (event.clientX - rect.left) / rect.width - 0.5;
        const py = (event.clientY - rect.top) / rect.height - 0.5;
        rotY(px * max * 2);
        rotX(-py * max * 2);
        glareX?.(px * 60);
        glareY?.(py * 60);
        if (glareRef.current)
          gsap.to(glareRef.current, { opacity: 0.35, duration: DURATION.micro });
      };
      const onLeave = () => {
        rotX(0);
        rotY(0);
        if (glareRef.current) gsap.to(glareRef.current, { opacity: 0, duration: DURATION.fast });
      };

      el.addEventListener('pointermove', onMove);
      el.addEventListener('pointerleave', onLeave);
      return () => {
        el.removeEventListener('pointermove', onMove);
        el.removeEventListener('pointerleave', onLeave);
      };
    },
    { scope: ref, dependencies: [reduced, max] },
  );

  return (
    <div
      ref={ref}
      className={cn('[transform-style:preserve-3d]', className)}
      style={{ perspective }}
    >
      <div ref={innerRef} className="relative h-full w-full [transform-style:preserve-3d]">
        {children}
        {glare && (
          <div
            ref={glareRef}
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 opacity-0 [mix-blend-mode:soft-light] [background:radial-gradient(circle_at_center,var(--color-brand-gold-light),transparent_60%)]"
          />
        )}
      </div>
    </div>
  );
}
